/* eslint-disable @typescript-eslint/camelcase */
import React, { FC, useState, useRef } from 'react';
import { capitalize, debounce } from 'lodash';
import { Collection, Course, Lesson, Path, Playlist } from '@fenderdigital/api-definitions/play';
import usePlayUser from '@fenderdigital/custom-hooks/hooks/usePlayUser';
import { getVideoPlayerProps } from '../../../utils/curriculum-utils';
import { EventNames } from '../../../lib/analytics';
import VideoJSPlayer from '../../../components/VideoJSPlayer';
import { VideoJSPlayerHandle } from '../../../components/VideoJSPlayer/VideoJSPlayer';
import {
  trackVideoEvent,
  VideoSpeedTrackingProperties,
  trackWatchEvent,
  VideoEvent,
} from '../../../components/VideoJSPlayer/VideoJSPlayerAnalytics';
import { isLocked } from '../../../utils/access-utils';
import * as S from './LessonVideo-styled';

interface LessonVideoProps {
  collection?: Collection;
  course?: Course;
  lesson?: Lesson;
  path?: Path;
  playlist?: Playlist;
}

const LessonVideo: FC<LessonVideoProps> = ({ collection, course, lesson, path, playlist }) => {
  const { user, accessLevel: userAccessLevel } = usePlayUser();
  const videoProps = getVideoPlayerProps({ lesson });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const videoJSPlayerRef = useRef<VideoJSPlayerHandle | null>(null);
  const hasStarted = useRef(false);
  const isCourseLocked = isLocked(course?.['access-levels'], userAccessLevel);

  const userProperties = {
    fc_id: user?.userID,
  };

  const lessonProperties = {
    lesson_id: lesson?.id,
    lesson_slug: lesson?.slug,
    lesson_type: lesson?.type,
    lesson_name: lesson?.title,
  };

  const courseProperties = {
    course_id: course?.id || '',
    course_slug: course?.slug || '',
    course_name: course?.title || '',
    is_locked: String(isCourseLocked),
  };

  const pathProperties = {
    path: capitalize(path?.style ?? ''),
    instrument: lesson?.instrument,
    playlist: playlist?.['description-long'] || '',
    playlist_id: playlist?.id || '',
    level: lesson?.level,
    path_id: path?.id || '',
    genre: path?.style || '',
  };

  const collectionProperties = {
    collection_name: collection?.title || '',
    collection_id: collection?.id || '',
  };

  const onBufferComplete = (): void => trackEvent(VideoEvent.BUFFER_COMPLETE);

  const onFullscreen = (isVideoFullscreen: boolean): void => setIsFullscreen(isVideoFullscreen);

  const onPictureInPicture = (isPictureInPicture: boolean): void => {
    trackEvent(VideoEvent.PICTURE_IN_PICTURE, { mini_player_activate: isPictureInPicture });
  };

  const onPlaybackRateChange = (fromSpeed: number, toSpeed: number): void => {
    trackEvent(VideoEvent.SPEED, { from_speed: `${fromSpeed}x`, to_speed: `${toSpeed}x` });
  };

  const onSeekBarClick = (fromTimestamp: number, toTimestamp: number): void => {
    if (fromTimestamp < toTimestamp) {
      trackEvent(VideoEvent.SCRUB_FORWARD, {
        timestamp_from: fromTimestamp.toString(),
        timestamp_to: toTimestamp.toString(),
      });
    } else {
      trackEvent(VideoEvent.SCRUB_BACK, {
        timestamp_from: fromTimestamp.toString(),
        timestamp_to: toTimestamp.toString(),
      });
    }
  };

  const onWatching = (watchedToSeconds: number, watchedToPercentage: number): void => {
    const properties = {
      ...userProperties,
      ...lessonProperties,
      ...courseProperties,
      ...pathProperties,
      ...collectionProperties,
      watchedTo: watchedToPercentage,
      watched_to_seconds: watchedToSeconds,
      watched_to_percentage: watchedToPercentage,
      lesson: lesson?.title,
    };

    trackWatchEvent(EventNames.WATCHED_VIDEO, properties);
  };

  const trackEvent = (
    event: VideoEvent,
    trackingProperties?: {} | VideoSpeedTrackingProperties,
  ): void => {
    const player = videoJSPlayerRef.current?.getPlayer();

    const properties = {
      ...userProperties,
      ...lessonProperties,
      ...trackingProperties,
    };

    if (player) {
      switch (event) {
        case VideoEvent.BUFFER_COMPLETE:
          trackVideoEvent(EventNames.VIDEO_PLAYBACK_BUFFER_COMPLETED, player, properties);
          break;
        case VideoEvent.COMPLETE:
          trackVideoEvent(EventNames.VIDEO_PLAYBACK_COMPLETED, player, properties);
          break;
        case VideoEvent.PAUSE:
          trackVideoEvent(EventNames.VIDEO_PLAYBACK_PAUSED, player, properties);
          break;
        case VideoEvent.PICTURE_IN_PICTURE:
          trackVideoEvent(EventNames.MINI_PLAYER_TOGGLED, player, properties);
          break;
        case VideoEvent.PLAY:
          if (hasStarted.current === false) {
            trackVideoEvent(EventNames.VIDEO_PLAYBACK_STARTED, player, properties);
            hasStarted.current = true;
          } else {
            trackVideoEvent(EventNames.VIDEO_PLAYBACK_RESUMED, player, properties);
          }
          break;
        case VideoEvent.QUALITY_CHANGE:
          trackVideoEvent(EventNames.VIDEO_QUALITY_UPDATED, player, properties);
          break;
        case VideoEvent.SCRUB_BACK:
          trackVideoEvent(EventNames.VIDEO_PLAYER_SCRUB_BACKWARD, player, properties);
          break;
        case VideoEvent.SCRUB_FORWARD:
          trackVideoEvent(EventNames.VIDEO_PLAYER_SCRUB_FORWARD, player, properties);
          break;
        case VideoEvent.SEEK_BACK:
          trackVideoEvent(EventNames.VIDEO_PLAYER_BACK_10, player, properties);
          break;
        case VideoEvent.SEEK_FORWARD:
          trackVideoEvent(EventNames.VIDEO_PLAYER_FORWARD_10, player, properties);
          break;
        case VideoEvent.SPEED:
          trackVideoEvent(EventNames.VIDEO_PLAYER_SPEED_CHANGED, player, properties);
          break;
        default:
          break;
      }
    }
  };

  if (!videoProps) return null;

  return (
    <S.VideoContainer isFullscreen={isFullscreen}>
      <VideoJSPlayer
        ref={videoJSPlayerRef}
        poster={videoProps.poster}
        thumbnailBaseUrl={videoProps.thumbnailBaseUrl}
        videoSrc={videoProps.src}
        videoType={videoProps.type}
        onBufferComplete={debounce(onBufferComplete, 3000)}
        onComplete={(): void => trackEvent(VideoEvent.COMPLETE)}
        onFullscreen={onFullscreen}
        onPause={(): void => trackEvent(VideoEvent.PAUSE)}
        onPictureInPicture={onPictureInPicture}
        onPlay={(): void => trackEvent(VideoEvent.PLAY)}
        onPlaybackRateChange={onPlaybackRateChange}
        onQualityChange={(): void => trackEvent(VideoEvent.QUALITY_CHANGE)}
        onSeekBarClick={onSeekBarClick}
        onSeekBack={(fromTimestamp, toTimestamp): void =>
          trackEvent(VideoEvent.SEEK_BACK, {
            timestamp_from: Math.floor(fromTimestamp).toString(),
            timestamp_to: Math.floor(toTimestamp).toString(),
          })
        }
        onSeekForward={(fromTimestamp, toTimestamp): void =>
          trackEvent(VideoEvent.SEEK_FORWARD, {
            timestamp_from: Math.floor(fromTimestamp).toString(),
            timestamp_to: Math.floor(toTimestamp).toString(),
          })
        }
        onWatching={debounce(onWatching, 1000)}
      />
    </S.VideoContainer>
  );
};

export default LessonVideo;
